import React, { useState } from 'react';
import { Variable } from 'src/api/types';
import { IntelVariable } from 'src/components/riskIntel/types';
import { PositionedVariablesPopover } from 'src/components/riskIntel/comps/PositionedVariablesPopover';
import { customSuggestionsFilter, isWorkspaceOrGlobalVariable } from '../shared/formula';
import { getVariableIcon } from './variableIcon';

interface ChipVariablesPopoverProps {
  /** The clicked chip element the popover anchors to. */
  anchorEl: HTMLElement;
  variables: Variable[];
  /** Drives per-type row icons (workspace / assessment / override) — same as the chips. */
  workspaceVariableNames: Set<string>;
  /** The variable to open into / scroll to / pulse inside the list. */
  focusVariableId: number;
  onClose: () => void;
  onSaveVariableValue?: (config: { variableId: number; value: number; currency: boolean }) => void | Promise<void>;
  onAddWorkspaceVariable?: () => void;
  onAddAssessmentVariable?: () => void;
}

// Map the form's variables onto the shape the shared popover expects, reusing the suggestion filter
// to drop workspace variables that have an assessment override.
const toIntelVariables = (variables: Variable[]): IntelVariable[] =>
  customSuggestionsFilter('', variables).map((v) => ({
    id: v.id,
    name: v.alias || v.name,
    value: v.value ?? null,
    scope: isWorkspaceOrGlobalVariable(v) ? 'workspace' : 'assessment',
    currency: !!v.currency,
  }));

/**
 * Scenario-form chip → the shared "edit variables" popup, anchored to the clicked chip and
 * pre-targeted to its variable. Thin wrapper over PositionedVariablesPopover: it maps the form's
 * `Variable` shape, persists via `onSaveVariableValue`, and shows per-type icons.
 */
export const ChipVariablesPopover: React.FC<ChipVariablesPopoverProps> = ({
  anchorEl,
  variables,
  workspaceVariableNames,
  focusVariableId,
  onClose,
  onSaveVariableValue,
  onAddWorkspaceVariable,
  onAddAssessmentVariable,
}) => {
  const [saving, setSaving] = useState(false);

  return (
    <PositionedVariablesPopover
      anchorEl={anchorEl}
      variables={toIntelVariables(variables)}
      pending={saving}
      initialEditId={focusVariableId}
      onPopulate={async (variableId, value, currency) => {
        setSaving(true);
        try {
          await onSaveVariableValue?.({ variableId, value, currency });
        } finally {
          setSaving(false);
        }
      }}
      onAddWorkspaceVariable={onAddWorkspaceVariable ?? (() => {})}
      onAddAssessmentVariable={onAddAssessmentVariable ?? (() => {})}
      renderIcon={(iv) => {
        const v = variables.find((x) => x.id === iv.id);
        return v ? getVariableIcon(v, workspaceVariableNames) : null;
      }}
      onClose={onClose}
    />
  );
};
